import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AlertCircle, Plus, ArrowLeft, CheckCircle, ArrowUpCircle, MessageCircle, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComplaintsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddComplaint, setShowAddComplaint] = useState(false);
  const [newComplaint, setNewComplaint] = useState({ subject: '', description: '', kid_id: '' });
  const [showRespondDialog, setShowRespondDialog] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [responseText, setResponseText] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [complaintsRes, kidsRes] = await Promise.all([
        axios.get(`${API_URL}/complaints`, { headers: { Authorization: `Bearer ${token}` } }),
        user.role === 'parent' 
          ? axios.get(`${API_URL}/parent/kids`, { headers: { Authorization: `Bearer ${token}` } })
          : Promise.resolve({ data: [] })
      ]);
      setComplaints(complaintsRes.data);
      setKids(kidsRes.data);
    } catch (error) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  }, [token, user.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitComplaint = async () => {
    if (!newComplaint.kid_id || !newComplaint.subject.trim() || !newComplaint.description.trim()) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/complaints`, newComplaint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Complaint submitted. The driver will be notified first.');
      setShowAddComplaint(false);
      setNewComplaint({ subject: '', description: '', kid_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit complaint');
    }
  };

  const handleEscalate = async (complaintId) => {
    try {
      const res = await axios.put(`${API_URL}/complaints/${complaintId}/escalate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to escalate complaint');
    }
  };

  const handleRespond = async () => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    try {
      await axios.put(`${API_URL}/complaints/${selectedComplaint.complaint_id}/respond`, 
        { response: responseText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Response submitted');
      setShowRespondDialog(false);
      setResponseText('');
      setSelectedComplaint(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };

  const getEscalationBadge = (level) => {
    const levels = {
      1: { label: 'Driver', color: 'bg-blue-500' },
      2: { label: 'Transport', color: 'bg-amber-500' },
      3: { label: 'Association', color: 'bg-red-500' }
    };
    return levels[level] || { label: 'Unknown', color: 'bg-gray-500' };
  };

  const getStatusBadge = (status) => {
    const statuses = {
      'driver_pending': { label: 'Awaiting Driver', color: 'bg-blue-500' },
      'transport_pending': { label: 'Awaiting Transport', color: 'bg-amber-500' },
      'association_pending': { label: 'With Association', color: 'bg-red-500' },
      'resolved': { label: 'Resolved', color: 'bg-emerald-500' }
    };
    return statuses[status] || { label: status, color: 'bg-gray-500' };
  };

  const canRespond = (complaint) => {
    if (user.role === 'driver' && complaint.escalation_level === 1) return true;
    if (user.role === 'transport' && complaint.escalation_level === 2) return true;
    if ((user.role === 'admin' || user.role === 'master_admin') && complaint.status !== 'resolved') return true;
    return false;
  };

  const canEscalate = (complaint) => {
    if (user.role !== 'parent') return false;
    if (complaint.status === 'resolved') return false;
    if (complaint.escalation_level >= 3) return false;
    // Can escalate if current level has responded or after some time
    if (complaint.escalation_level === 1 && complaint.driver_response) return true;
    if (complaint.escalation_level === 2 && complaint.transport_response) return true;
    // Allow escalate even without response (for now)
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading complaints...</p>
        </div>
      </div>
    );
  }

  const roleTitle = {
    'parent': 'My Complaints',
    'driver': 'Complaints About My Service',
    'transport': 'Company Complaints',
    'admin': 'All Complaints',
    'master_admin': 'All Complaints'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white p-6" data-testid="complaints-page">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/')} className="rounded-full mb-4" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold" data-testid="page-title">
                {roleTitle[user.role] || 'Complaints'}
              </h1>
              <p className="text-muted-foreground mt-1">
                {user.role === 'parent' 
                  ? 'Complaints are first sent to the driver, then transport operator, then association' 
                  : 'Review and respond to complaints'}
              </p>
            </div>
            {user.role === 'parent' && (
              <Dialog open={showAddComplaint} onOpenChange={setShowAddComplaint}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-complaint-button">
                    <Plus className="w-4 h-4 mr-2" />
                    New Complaint
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-complaint-dialog">
                  <DialogHeader>
                    <DialogTitle>Submit Complaint</DialogTitle>
                    <DialogDescription>
                      Your complaint will first be sent to the driver. If not resolved, you can escalate to transport operator, then the association.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Select Child</Label>
                      <Select value={newComplaint.kid_id} onValueChange={(v) => setNewComplaint({ ...newComplaint, kid_id: v })}>
                        <SelectTrigger data-testid="select-kid">
                          <SelectValue placeholder="Select a child" />
                        </SelectTrigger>
                        <SelectContent>
                          {kids.map((kid) => (
                            <SelectItem key={kid.kid_id} value={kid.kid_id}>{kid.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        value={newComplaint.subject}
                        onChange={(e) => setNewComplaint({ ...newComplaint, subject: e.target.value })}
                        placeholder="Brief description of the issue"
                        data-testid="complaint-subject-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={newComplaint.description}
                        onChange={(e) => setNewComplaint({ ...newComplaint, description: e.target.value })}
                        placeholder="Detailed explanation of the issue"
                        rows={5}
                        data-testid="complaint-description-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleSubmitComplaint} data-testid="submit-complaint-button">Submit Complaint</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <Card className="shadow-lg" data-testid="complaints-list">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Complaints ({complaints.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {complaints.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-complaints">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>No complaints</p>
              </div>
            ) : (
              <div className="space-y-4">
                {complaints.map((complaint) => {
                  const statusInfo = getStatusBadge(complaint.status);
                  const levelInfo = getEscalationBadge(complaint.escalation_level);
                  
                  return (
                    <div
                      key={complaint.complaint_id}
                      className="p-4 border rounded-xl hover:shadow-md transition-all"
                      data-testid={`complaint-item-${complaint.complaint_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-semibold text-lg">{complaint.subject}</h3>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                            <Badge variant="outline">Level {complaint.escalation_level}: {levelInfo.label}</Badge>
                          </div>
                          
                          {/* Kid and Parent Info */}
                          {complaint.kid_name && (
                            <p className="text-sm text-muted-foreground mb-1">
                              Child: {complaint.kid_name}
                            </p>
                          )}
                          {(user.role !== 'parent') && complaint.parent_name && (
                            <p className="text-sm text-muted-foreground mb-1">
                              Parent: {complaint.parent_name}
                            </p>
                          )}
                          
                          <p className="text-sm my-3 p-3 bg-slate-50 rounded-lg">{complaint.description}</p>
                          
                          {/* Driver Response */}
                          {complaint.driver_response && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                              <p className="text-xs font-semibold text-blue-700 mb-1">Driver Response:</p>
                              <p className="text-sm">{complaint.driver_response}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {complaint.driver_responded_at && formatDateTime(complaint.driver_responded_at)}
                              </p>
                            </div>
                          )}
                          
                          {/* Transport Response */}
                          {complaint.transport_response && (
                            <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                              <p className="text-xs font-semibold text-amber-700 mb-1">Transport Operator Response:</p>
                              <p className="text-sm">{complaint.transport_response}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {complaint.transport_responded_at && formatDateTime(complaint.transport_responded_at)}
                              </p>
                            </div>
                          )}
                          
                          {/* Admin Response */}
                          {complaint.admin_response && (
                            <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
                              <p className="text-xs font-semibold text-emerald-700 mb-1">Association Response:</p>
                              <p className="text-sm">{complaint.admin_response}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Resolved: {complaint.resolved_at && formatDateTime(complaint.resolved_at)}
                              </p>
                            </div>
                          )}
                          
                          <p className="text-xs text-muted-foreground mt-3">
                            Created: {formatDateTime(complaint.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 mt-3 pt-3 border-t">
                        {/* Parent can escalate */}
                        {canEscalate(complaint) && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="rounded-full text-amber-600"
                            onClick={() => handleEscalate(complaint.complaint_id)}
                            data-testid={`escalate-${complaint.complaint_id}`}
                          >
                            <ArrowUpCircle className="w-4 h-4 mr-2" />
                            Escalate to {complaint.escalation_level === 1 ? 'Transport' : 'Association'}
                          </Button>
                        )}
                        
                        {/* Driver/Transport/Admin can respond */}
                        {canRespond(complaint) && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="rounded-full"
                            onClick={() => { setSelectedComplaint(complaint); setShowRespondDialog(true); }}
                            data-testid={`respond-${complaint.complaint_id}`}
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Respond
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Respond Dialog */}
        <Dialog open={showRespondDialog} onOpenChange={setShowRespondDialog}>
          <DialogContent data-testid="respond-dialog">
            <DialogHeader>
              <DialogTitle>Respond to Complaint</DialogTitle>
              <DialogDescription>
                {selectedComplaint?.subject}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Your Response</Label>
                <Textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Enter your response to this complaint..."
                  rows={5}
                  data-testid="response-textarea"
                />
              </div>
              {(user.role === 'admin' || user.role === 'master_admin') && (
                <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
                  <AlertCircle className="w-4 h-4 inline-block mr-2" />
                  As an administrator, your response will mark this complaint as resolved.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRespondDialog(false); setResponseText(''); }}>Cancel</Button>
              <Button onClick={handleRespond} data-testid="submit-response-button">
                <Send className="w-4 h-4 mr-2" />
                {(user.role === 'admin' || user.role === 'master_admin') ? 'Resolve Complaint' : 'Send Response'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
